import Config from './config';

export interface RuntimeConfig {
  graphqlEndpoint?: string;
  /**
   * Base URL for the REST API (`/api/admin/*`).
   *
   * Separate from graphqlEndpoint because they are separate clients, and
   * overriding only GraphQL is a trap: login and the GraphQL screens follow the
   * runtime value while every REST screen keeps the build-time one. In a review
   * app that meant Dashboard V2 - which is REST-only - was reading a completely
   * different environment.
   */
  restEndpoint?: string;
  /**
   * Tenant key to resolve instead of deriving one from the hostname.
   *
   * The app normally uses the first DNS label, which works when the host IS the
   * tenant (web-qa.gravywork.com -> "web-qa"). A review app is served from
   * admin-<slug>.review.gravywork.com, whose first label matches no tenant, so
   * without this the app redirects straight to /invalid-tenant and the login
   * form never renders.
   */
  tenantKey?: string;
}

let loaded: RuntimeConfig | null = null;

/**
 * Fetches /config.json, which sits beside the bundle in S3 and differs per
 * environment. This is what lets one build artifact serve the baseline and
 * every review app: the same JS is copied to N prefixes, each with its own
 * config.json.
 *
 * Failure is never fatal. Production has no config.json and must keep using the
 * value webpack baked in — otherwise adding a runtime fetch for the benefit of
 * review apps would introduce a new production failure mode.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (response.ok) {
      loaded = (await response.json()) as RuntimeConfig;
    } else {
      loaded = {};
    }
  } catch {
    loaded = {};
  }
  return loaded;
}

/**
 * The GraphQL endpoint, preferring the runtime value and falling back to the
 * build-time one. Safe to call before loadRuntimeConfig() resolves.
 */
export function getGraphqlEndpoint(): string {
  return loaded?.graphqlEndpoint || Config.GRAPHQL_ENDPOINT;
}

/**
 * The REST base URL, preferring the runtime value and falling back to the
 * build-time one. Safe to call before loadRuntimeConfig() resolves.
 */
export function getRestEndpoint(): string {
  return loaded?.restEndpoint || Config.BASE_RUBY_URL || '';
}

/**
 * The tenant key, preferring the runtime value, then the build-time override,
 * then the hostname. Ordering matters: existing environments have no
 * config.json and must keep resolving exactly as before.
 */
export function getTenantKey(): string {
  return (
    loaded?.tenantKey ||
    process.env.SUB_DOMAIN ||
    window.location.hostname.split('.')[0]
  );
}

/** Test-only hook. */
export function __resetRuntimeConfigForTests(): void {
  loaded = null;
}
